import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { toDate } from 'shared/utilities';
import { DateType } from 'shared/types';
import { useMedia } from 'shared/hooks';

import { Store } from 'pages/calendar/store';
import { CALENDAR_PAGE } from 'pages/calendar/constants/CALENDAR_PAGE';
import { CalendarPageCreatingPopout } from 'pages/calendar/components/CalendarPageCreatingPopout';
import { CalendarPageDaysCalendarItem } from 'pages/calendar/components/CalendarPageDaysCalendarItem';

import { getCalendarDayRows } from './utilities/getCalendarDayRows';
import { CalendarPageDaysTimeline } from './components/CalendarPageDaysTimeline';

import './styles.scss';

type PropsType = {
    date: DateType;
};

export const CalendarPageDaysDay = observer(function CalendarPageDaysDay({ date }: PropsType) {
    const {is320} = useMedia();
    const calendarItems = Store.calendarItems.filter(calendarItem => {
        if (calendarItem.isAllDayEvent) {
            return false;
        }

        const from = date.set('hour', CALENDAR_PAGE.HOURS[0] - 1).set('minute', 59);
        const to = date
            .set('hour', CALENDAR_PAGE.HOURS[CALENDAR_PAGE.HOURS.length - 1])
            .set('minute', 0)
            .add(1, 'hour');

        return toDate(calendarItem.startAt).isAfter(from) && toDate(calendarItem.endAt).isBefore(to);
    });

    const calendarDayRows = getCalendarDayRows(calendarItems);

    const padding = is320 ? 0 : 12;

    return (
        <div
            className={classnames('calendar-page-days-day', {
                'calendar-page-days-day--weekend': date.day() === 0 || date.day() === 6,
            })}
        >
            {CALENDAR_PAGE.HOURS.map(hour => (
                <div key={hour} className={`calendar-page-days-day-cell calendar-page-days-day-cell--${hour}`}>
                    <CalendarPageCreatingPopout
                        date={date.set('hour', hour).set('minute', 0)}
                        render={({ onClick, isOpened }) => (
                            <div
                                onClick={onClick}
                                className={classnames('calendar-page-days-day-cell__half', {
                                    'calendar-page-days-day-cell__half--opened': isOpened,
                                })}
                            />
                        )}
                    />
                    <CalendarPageCreatingPopout
                        date={date.set('hour', hour).set('minute', 30)}
                        className={'calendar-page-days-day-cell__half'}
                        render={({ onClick, isOpened }) => (
                            <div
                                onClick={onClick}
                                className={classnames('calendar-page-days-day-cell__half', {
                                    'calendar-page-days-day-cell__half--opened': isOpened,
                                })}
                            />
                        )}
                    />
                </div>
            ))}
            <CalendarPageDaysTimeline date={date} />
            {calendarDayRows.map(calendarDayRow => {
                return calendarDayRow.calendarDayColumns.map((calendarDayColumn, index) => {
                    return calendarDayColumn.calendarItems.map(calendarItem => {
                        return (
                            <CalendarPageDaysCalendarItem
                                key={calendarItem.id}
                                style={{
                                    position: 'absolute',
                                    top: (CALENDAR_PAGE.HOUR_HEIGHT * calendarItem.startAtMinutes) / 60 - CALENDAR_PAGE.HOURS[0] * CALENDAR_PAGE.HOUR_HEIGHT,
                                    height: -1 + (CALENDAR_PAGE.HOUR_HEIGHT * (calendarItem.endAtMinutes - calendarItem.startAtMinutes)) / 60,
                                    width: `calc((100% - ${padding}px) / ${calendarDayRow.calendarDayColumns.length})`,
                                    left: `calc(((100% - ${padding}px) / ${calendarDayRow.calendarDayColumns.length}) * ${index})`,
                                }}
                                calendarItem={calendarItem}
                            />
                        );
                    });
                });
            })}
        </div>
    );
});
